import { useState, useEffect, useRef } from 'react';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import GitHubIcon from '@mui/icons-material/GitHub';
import GoogleIcon from '@mui/icons-material/Google';
import HubIcon from '@mui/icons-material/Hub';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import SyncIcon from '@mui/icons-material/Sync';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { QbConnection } from '@/services/financeQuickBooks';
import { WarpConnectionStatus, WarpWorker } from '@/services/financeWarp';
import { GithubConnection, GithubRepo } from '@/services/github';
import { GoogleAdsConnectionStatus, GoogleAdsCustomer } from '@/services/googleAds';
import { PipedriveConnectionStatus } from '@/services/pipedrive';
import { GoogleConnectionStatus } from '@/services/searchConsole';
import { AhrefsConnectionStatus } from '@/services/seoAhrefs';
import { SmartleadConnectionStatus } from '@/services/smartlead';
import { FxBankConnection } from '@/types/api/finance';

type Props = {
  connectionStatus: GoogleConnectionStatus | null;
  isLoading: boolean;
  isConnecting: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
  plaidConnections: FxBankConnection[];
  isPlaidConnecting: boolean;
  plaidLinkToken: string | null;
  onPlaidConnect: () => void;
  onPlaidSuccess: (publicToken: string) => void;
  onPlaidExit: () => void;
  onPlaidDisconnect: (connectionId: string) => void;
  pipedriveStatus: PipedriveConnectionStatus | null;
  isPipedriveConnecting: boolean;
  pipedriveError: string | null;
  onPipedriveConnect: (apiToken: string) => void;
  onPipedriveDisconnect: () => void;
  smartleadStatus: SmartleadConnectionStatus | null;
  isSmartleadConnecting: boolean;
  smartleadError: string | null;
  onSmartleadConnect: (apiKey: string) => void;
  onSmartleadDisconnect: () => void;
  githubConnection: GithubConnection | null;
  isGithubConnecting: boolean;
  githubRepos: GithubRepo[];
  onGithubConnect: () => void;
  onGithubCancelSetup: () => void;
  onGithubRepoSelect: (repo: GithubRepo) => void;
  onGithubManualRepo: (owner: string, name: string) => void;
  onGithubPatConnect: (token: string, owner: string, name: string) => Promise<void>;
  onGithubDisconnect: () => void;
  googleAdsStatus: GoogleAdsConnectionStatus | null;
  isGoogleAdsLoading: boolean;
  googleAdsCustomers: GoogleAdsCustomer[];
  googleAdsError: string | null;
  onGoogleAdsListCustomers: () => void;
  onGoogleAdsSelectCustomer: (customer: GoogleAdsCustomer) => void;
  onGoogleAdsDisconnect: () => void;
  onGoogleAdsTest: () => void;
  googleAdsTestResult: string | null;
  ahrefsStatus: AhrefsConnectionStatus | null;
  isAhrefsConnecting: boolean;
  ahrefsError: string | null;
  onAhrefsConnect: (apiKey: string) => void;
  onAhrefsDisconnect: () => void;
  isAhrefsSyncing: boolean;
  onAhrefsSync: () => void;
  qbConnections: QbConnection[];
  isQbConnecting: boolean;
  isQbSyncing: boolean;
  qbSyncResult: string | null;
  onQbConnect: () => void;
  onQbSync: (
    connectionId: string,
    startDate?: string,
    endDate?: string,
    syncType?: 'all' | 'transactions' | 'invoices'
  ) => void;
  onQbDisconnect: (connectionId: string) => void;
  warpStatus: WarpConnectionStatus | null;
  warpContractors: WarpWorker[];
  isWarpConnecting: boolean;
  isWarpSyncing: boolean;
  isWarpLinking: boolean;
  warpError: string | null;
  warpResult: string | null;
  warpCreateProfiles: boolean;
  onWarpCreateProfilesChange: (value: boolean) => void;
  onWarpConnect: (apiKey: string) => void;
  onWarpSync: () => void;
  onWarpLink: () => void;
  onWarpDisconnect: () => void;
};

/**
 * Loads the Plaid Link SDK from CDN and opens the Link modal.
 */
function PlaidLinkOpener({
  linkToken,
  onSuccess,
  onExit,
}: {
  linkToken: string;
  onSuccess: (publicToken: string) => void;
  onExit: () => void;
}): null {
  const openedRef = useRef(false);

  useEffect(() => {
    if (openedRef.current) return;

    const SCRIPT_ID = 'plaid-link-sdk';

    function openPlaidLink(): void {
      const { Plaid } = window as unknown as Record<string, unknown>;
      if (!Plaid) return;
      openedRef.current = true;

      const handler = (Plaid as Record<string, (...args: unknown[]) => unknown>).create({
        token: linkToken,
        onSuccess: (publicToken: string) => onSuccess(publicToken),
        onExit: () => onExit(),
      });
      (handler as Record<string, (...args: unknown[]) => unknown>).open();
    }

    if ((window as unknown as Record<string, unknown>).Plaid) {
      openPlaidLink();
      return;
    }

    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
      script.onload = openPlaidLink;
      document.head.appendChild(script);
    }
  }, [linkToken, onSuccess, onExit]);

  return null;
}

/**
 * GitHub PAT-based connection: token + repo input.
 */
function GitHubPatSetup({
  isConnecting,
  onPatConnect,
}: {
  isConnecting: boolean;
  onPatConnect: (token: string, owner: string, name: string) => Promise<void>;
}): JSX.Element {
  const [token, setToken] = useState('');
  const [repo, setRepo] = useState('');
  const [error, setError] = useState('');

  const handleConnect = async (): Promise<void> => {
    const parts = repo.trim().split('/');
    if (parts.length !== 2 || !parts[0] || !parts[1]) {
      setError('Enter repo as owner/name (e.g. usetrio/zion)');
      return;
    }
    if (!token.trim()) {
      setError('Token is required');
      return;
    }
    setError('');
    try {
      await onPatConnect(token.trim(), parts[0], parts[1]);
    } catch {
      setError('Invalid token or no access to this repository.');
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Create a <strong>Fine-grained Personal Access Token</strong> at{' '}
        <a
          href="https://github.com/settings/tokens?type=beta"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#054AD9' }}
        >
          GitHub → Settings → Developer Settings → Fine-grained tokens
        </a>
        . Select the repository and grant <strong>Contents: Read-only</strong> permission.
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <TextField
          size="small"
          label="Repository"
          placeholder="owner/repo (e.g. usetrio/zion)"
          value={repo}
          onChange={(e) => setRepo(e.target.value)}
          fullWidth
          sx={{ '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: 13 } }}
        />
        <TextField
          size="small"
          label="Personal Access Token"
          placeholder="github_pat_xxxxxxxxxxxx"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          type="password"
          fullWidth
          error={!!error}
          helperText={error}
          onKeyDown={(e) => e.key === 'Enter' && handleConnect()}
          sx={{ '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: 13 } }}
        />
        <Button
          variant="contained"
          startIcon={<GitHubIcon />}
          onClick={handleConnect}
          disabled={!token.trim() || !repo.includes('/') || isConnecting}
          sx={{
            backgroundColor: '#24292f',
            '&:hover': { backgroundColor: '#1b1f23' },
            textTransform: 'none',
            alignSelf: 'flex-start',
          }}
        >
          {isConnecting ? 'Connecting...' : 'Connect Repository'}
        </Button>
      </Box>
    </Box>
  );
}

/**
 * GitHub repo selection after OAuth callback.
 */
function GitHubRepoSelect({
  repos,
  isConnecting,
  onRepoSelect,
  onCancel,
  onDisconnect,
  onManualSubmit,
}: {
  repos: GithubRepo[];
  isConnecting: boolean;
  onRepoSelect: (repo: GithubRepo) => void;
  onCancel: () => void;
  onDisconnect: () => void;
  onManualSubmit: (owner: string, name: string) => void;
}): JSX.Element {
  const [repoFilter, setRepoFilter] = useState('');
  const [manualRepo, setManualRepo] = useState('');
  const [showManual, setShowManual] = useState(false);

  const filteredRepos = repoFilter
    ? repos.filter((r) => r.fullName.toLowerCase().includes(repoFilter.toLowerCase()))
    : repos;

  const handleManualSubmit = (): void => {
    const parts = manualRepo.trim().split('/');
    if (parts.length === 2 && parts[0] && parts[1]) {
      onManualSubmit(parts[0], parts[1]);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
        Select a repository
      </Typography>
      <TextField
        size="small"
        placeholder="Filter repos..."
        value={repoFilter}
        onChange={(e) => setRepoFilter(e.target.value)}
        fullWidth
        sx={{ mb: 1 }}
      />
      <Box
        sx={{
          maxHeight: 240,
          overflowY: 'auto',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        {filteredRepos.map((repo) => (
          <Box
            key={repo.fullName}
            onClick={() => !isConnecting && onRepoSelect(repo)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              px: 1.5,
              py: 1,
              cursor: isConnecting ? 'wait' : 'pointer',
              borderBottom: '1px solid',
              borderColor: 'divider',
              '&:last-child': { borderBottom: 'none' },
              '&:hover': { backgroundColor: '#F4F4F5' },
              '&:active': { backgroundColor: '#E4E4E7' },
              transition: 'background 200ms cubic-bezier(0.2, 0.8, 0.4, 1)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              {repo.isPrivate ? (
                <LockIcon sx={{ fontSize: 14, color: '#71717A', flexShrink: 0 }} />
              ) : (
                <PublicIcon sx={{ fontSize: 14, color: '#71717A', flexShrink: 0 }} />
              )}
              <Typography variant="body2" noWrap>
                {repo.fullName}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              {repo.defaultBranch}
            </Typography>
          </Box>
        ))}
        {filteredRepos.length === 0 && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No repos match your filter
            </Typography>
          </Box>
        )}
      </Box>

      {!showManual ? (
        <Button
          size="small"
          onClick={() => setShowManual(true)}
          sx={{ mt: 1, textTransform: 'none', color: '#71717A' }}
        >
          Don&apos;t see your repo? Enter manually
        </Button>
      ) : (
        <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            size="small"
            placeholder="owner/repo (e.g. usetrio/zion)"
            value={manualRepo}
            onChange={(e) => setManualRepo(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleManualSubmit()}
            fullWidth
            sx={{ '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: 13 } }}
          />
          <Button
            variant="contained"
            onClick={handleManualSubmit}
            disabled={!manualRepo.includes('/') || isConnecting}
            sx={{
              backgroundColor: '#24292f',
              '&:hover': { backgroundColor: '#1b1f23' },
              textTransform: 'none',
              whiteSpace: 'nowrap',
            }}
          >
            Connect
          </Button>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
        <Button size="small" onClick={onCancel} sx={{ textTransform: 'none', color: '#71717A' }}>
          Cancel
        </Button>
        <Button
          size="small"
          color="error"
          startIcon={<LinkOffIcon />}
          onClick={onDisconnect}
          sx={{ textTransform: 'none' }}
        >
          Disconnect
        </Button>
      </Box>
    </Box>
  );
}

/** Badge showing whether a synced Warp worker is linked to a vendor or profile. */
function WarpLinkBadge({ worker }: { worker: WarpWorker }): JSX.Element {
  if (worker.vendorId) {
    return (
      <Chip label="Vendor" size="small" color="success" variant="outlined" sx={{ fontSize: 10 }} />
    );
  }
  if (worker.profileId) {
    return <Chip label="Profile" size="small" variant="outlined" sx={{ fontSize: 10 }} />;
  }
  return (
    <Typography variant="caption" color="text.secondary">
      —
    </Typography>
  );
}

const IntegrationsComponent = function ({
  connectionStatus,
  isLoading,
  isConnecting,
  onConnect,
  onDisconnect,
  plaidConnections,
  isPlaidConnecting,
  plaidLinkToken,
  onPlaidConnect,
  onPlaidSuccess,
  onPlaidExit,
  onPlaidDisconnect,
  pipedriveStatus,
  isPipedriveConnecting,
  pipedriveError,
  onPipedriveConnect,
  onPipedriveDisconnect,
  smartleadStatus,
  isSmartleadConnecting,
  smartleadError,
  onSmartleadConnect,
  onSmartleadDisconnect,
  githubConnection,
  isGithubConnecting,
  githubRepos,
  onGithubConnect,
  onGithubCancelSetup,
  onGithubRepoSelect,
  onGithubManualRepo,
  onGithubPatConnect,
  onGithubDisconnect,
  googleAdsStatus,
  isGoogleAdsLoading,
  googleAdsCustomers,
  googleAdsError,
  onGoogleAdsListCustomers,
  onGoogleAdsSelectCustomer,
  onGoogleAdsDisconnect,
  onGoogleAdsTest,
  googleAdsTestResult,
  ahrefsStatus,
  isAhrefsConnecting,
  ahrefsError,
  onAhrefsConnect,
  onAhrefsDisconnect,
  isAhrefsSyncing,
  onAhrefsSync,
  qbConnections,
  isQbConnecting,
  isQbSyncing,
  qbSyncResult,
  onQbConnect,
  onQbSync,
  onQbDisconnect,
  warpStatus,
  warpContractors,
  isWarpConnecting,
  isWarpSyncing,
  isWarpLinking,
  warpError,
  warpResult,
  warpCreateProfiles,
  onWarpCreateProfilesChange,
  onWarpConnect,
  onWarpSync,
  onWarpLink,
  onWarpDisconnect,
}: Props): JSX.Element {
  const [pipedriveToken, setPipedriveToken] = useState('');
  const [smartleadKey, setSmartleadKey] = useState('');
  const [ahrefsKey, setAhrefsKey] = useState('');
  const [warpKey, setWarpKey] = useState('');
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const isConnected = connectionStatus?.isConnected;
  const hasPlaidConnections = plaidConnections.length > 0;
  const totalAccounts = plaidConnections.reduce((sum, c) => sum + (c.accounts?.length ?? 0), 0);
  const isGithubConnected = !!githubConnection;

  return (
    <Box sx={{}}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
        Integrations
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Connect third-party services to use across the platform. Once connected, any module (SEOx,
        FinanceX, CEOx, etc.) can use these integrations.
      </Typography>

      {/* GitHub */}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          p: 3,
          backgroundColor: '#fff',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <GitHubIcon sx={{ fontSize: 28, color: '#24292f' }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                GitHub
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Repository Access
              </Typography>
            </Box>
          </Box>
          {isGithubConnected && (
            <Chip label="Connected" color="success" size="small" icon={<CheckCircleIcon />} />
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Connect a GitHub repository to give Zion read access to your codebase.
        </Typography>

        {isGithubConnected && githubRepos.length === 0 && (
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1,
                px: 1.5,
                borderRadius: 1,
                backgroundColor: '#F4F4F5',
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  {githubConnection.repoOwner}/{githubConnection.repoName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Branch: {githubConnection.branch}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<GitHubIcon />}
                  onClick={onGithubConnect}
                  disabled={isGithubConnecting}
                  sx={{ textTransform: 'none' }}
                >
                  Reconnect
                </Button>
                <Button
                  size="small"
                  color="error"
                  startIcon={<LinkOffIcon />}
                  onClick={onGithubDisconnect}
                  sx={{ textTransform: 'none' }}
                >
                  Disconnect
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {!isGithubConnected && githubRepos.length === 0 && (
          <GitHubPatSetup isConnecting={isGithubConnecting} onPatConnect={onGithubPatConnect} />
        )}

        {githubRepos.length > 0 && (
          <GitHubRepoSelect
            repos={githubRepos}
            isConnecting={isGithubConnecting}
            onRepoSelect={onGithubRepoSelect}
            onManualSubmit={onGithubManualRepo}
            onCancel={onGithubCancelSetup}
            onDisconnect={onGithubDisconnect}
          />
        )}
      </Box>

      {/* Google */}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          p: 3,
          backgroundColor: '#fff',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <GoogleIcon sx={{ fontSize: 28, color: '#4285F4' }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Google
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Search Console, Analytics &amp; Google Ads
              </Typography>
            </Box>
          </Box>
          {isConnected && (
            <Chip label="Connected" color="success" size="small" icon={<CheckCircleIcon />} />
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Grants read-only access to Google Search Console, Analytics, and Google Ads data. Used by
          SEOx for rankings and traffic, and CEOx for ad campaign performance.
        </Typography>

        {!isConnected ? (
          <Button
            variant="contained"
            startIcon={<GoogleIcon />}
            onClick={onConnect}
            disabled={isConnecting}
            sx={{
              backgroundColor: '#4285F4',
              '&:hover': { backgroundColor: '#3367D6' },
              textTransform: 'none',
            }}
          >
            {isConnecting ? 'Connecting...' : 'Connect Google Account'}
          </Button>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Connected since{' '}
              {connectionStatus?.connectedAt
                ? new Date(connectionStatus.connectedAt).toLocaleDateString()
                : 'unknown'}
            </Typography>
            <Button
              size="small"
              color="error"
              startIcon={<LinkOffIcon />}
              onClick={onDisconnect}
              sx={{ textTransform: 'none' }}
            >
              Disconnect
            </Button>
          </Box>
        )}
      </Box>

      {/* Google Ads Account */}
      {isConnected && (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            p: 3,
            backgroundColor: '#fff',
            mb: 3,
          }}
        >
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <GoogleIcon sx={{ fontSize: 28, color: '#34A853' }} />
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  Google Ads
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Campaign Performance
                </Typography>
              </Box>
            </Box>
            {googleAdsStatus?.isConnected && (
              <Chip label="Connected" color="success" size="small" icon={<CheckCircleIcon />} />
            )}
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select which Google Ads account to use for the CEOx dashboard. Requires Google to be
            connected above.
          </Typography>

          {/* TEMP: Debug test button */}
          <Box
            sx={{
              mb: 2,
              p: 2,
              border: '1px dashed #E4E4E7',
              borderRadius: 1,
              backgroundColor: '#FAFAFA',
            }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={onGoogleAdsTest}
              sx={{ textTransform: 'none', mb: 1 }}
            >
              Test Google Ads API Connection
            </Button>
            {googleAdsTestResult && (
              <Box
                component="pre"
                sx={{
                  mt: 1,
                  p: 1.5,
                  fontSize: 11,
                  fontFamily: 'monospace',
                  backgroundColor: '#1e1e1e',
                  color: '#d4d4d4',
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: 300,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                }}
              >
                {googleAdsTestResult}
              </Box>
            )}
          </Box>

          {googleAdsError && (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
              {googleAdsError}
            </Typography>
          )}

          {googleAdsStatus?.isConnected && googleAdsCustomers.length === 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 1,
                  px: 1.5,
                  borderRadius: 1,
                  backgroundColor: '#F4F4F5',
                  flex: 1,
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {googleAdsStatus.customerName ?? googleAdsStatus.customerId}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Customer ID: {googleAdsStatus.customerId}
                  </Typography>
                </Box>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={onGoogleAdsListCustomers}
                disabled={isGoogleAdsLoading}
                sx={{ textTransform: 'none' }}
              >
                Change
              </Button>
              <Button
                size="small"
                color="error"
                startIcon={<LinkOffIcon />}
                onClick={onGoogleAdsDisconnect}
                sx={{ textTransform: 'none' }}
              >
                Disconnect
              </Button>
            </Box>
          )}

          {googleAdsCustomers.length > 0 && (
            <Box>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                Select an account:
              </Typography>
              <Box
                sx={{
                  maxHeight: 200,
                  overflowY: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                {googleAdsCustomers.map((c) => (
                  <Box
                    key={c.id}
                    onClick={() => !isGoogleAdsLoading && onGoogleAdsSelectCustomer(c)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 1.5,
                      py: 1,
                      cursor: isGoogleAdsLoading ? 'wait' : 'pointer',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 'none' },
                      '&:hover': { backgroundColor: '#F4F4F5' },
                      '&:active': { backgroundColor: '#E4E4E7' },
                      transition: 'background 200ms cubic-bezier(0.2, 0.8, 0.4, 1)',
                    }}
                  >
                    <Typography variant="body2">{c.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {c.id}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {!googleAdsStatus?.isConnected && googleAdsCustomers.length === 0 && (
            <Button
              variant="contained"
              onClick={onGoogleAdsListCustomers}
              disabled={isGoogleAdsLoading}
              sx={{
                backgroundColor: '#34A853',
                '&:hover': { backgroundColor: '#2D8F47' },
                textTransform: 'none',
              }}
            >
              {isGoogleAdsLoading ? 'Loading...' : 'Select Google Ads Account'}
            </Button>
          )}
        </Box>
      )}

      {/* Plaid */}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          p: 3,
          backgroundColor: '#fff',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AccountBalanceIcon sx={{ fontSize: 28, color: '#054AD9' }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Plaid
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Bank Transactions
              </Typography>
            </Box>
          </Box>
          {hasPlaidConnections && (
            <Chip label="Connected" color="success" size="small" icon={<CheckCircleIcon />} />
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Connect your bank accounts to automatically sync transactions into FinanceX. Supports
          checking, savings, and credit card accounts.
        </Typography>

        {hasPlaidConnections && (
          <Box sx={{ mb: 2 }}>
            {plaidConnections.map((conn) => (
              <Box
                key={conn.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1,
                  px: 1.5,
                  borderRadius: 1,
                  backgroundColor: '#F4F4F5',
                  mb: 1,
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {conn.institutionName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {conn.accounts?.length ?? 0} account
                    {(conn.accounts?.length ?? 0) !== 1 ? 's' : ''}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  color="error"
                  startIcon={<LinkOffIcon />}
                  onClick={() => onPlaidDisconnect(conn.id)}
                  sx={{ textTransform: 'none' }}
                >
                  Disconnect
                </Button>
              </Box>
            ))}
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={hasPlaidConnections ? <AddIcon /> : <AccountBalanceIcon />}
            onClick={onPlaidConnect}
            disabled={isPlaidConnecting}
            sx={{
              backgroundColor: '#054AD9',
              '&:hover': { backgroundColor: '#0440B8' },
              textTransform: 'none',
            }}
          >
            {(() => {
              if (isPlaidConnecting) return 'Connecting...';
              return hasPlaidConnections ? 'Add Another Bank' : 'Connect Bank Account';
            })()}
          </Button>
          {hasPlaidConnections && (
            <Typography variant="caption" color="text.secondary">
              {totalAccounts} account{totalAccounts !== 1 ? 's' : ''} connected
            </Typography>
          )}
        </Box>
      </Box>

      {/* Plaid Link modal opener */}
      {plaidLinkToken && (
        <PlaidLinkOpener
          linkToken={plaidLinkToken}
          onSuccess={onPlaidSuccess}
          onExit={onPlaidExit}
        />
      )}

      {/* Pipedrive */}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          p: 3,
          backgroundColor: '#fff',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <HubIcon sx={{ fontSize: 28, color: '#017737' }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Pipedrive
              </Typography>
              <Typography variant="caption" color="text.secondary">
                CRM &amp; Sales Pipeline
              </Typography>
            </Box>
          </Box>
          {pipedriveStatus?.isConnected && (
            <Chip label="Connected" color="success" size="small" icon={<CheckCircleIcon />} />
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Connect your Pipedrive account to pull deal pipeline, lead sources, and conversion metrics
          into the CEOx dashboard.
        </Typography>

        {pipedriveError && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {pipedriveError}
          </Typography>
        )}

        {!pipedriveStatus?.isConnected ? (
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <TextField
              size="small"
              placeholder="Paste your Pipedrive API token"
              value={pipedriveToken}
              onChange={(e) => setPipedriveToken(e.target.value)}
              type="password"
              sx={{
                flex: 1,
                maxWidth: 360,
                '& .MuiInputBase-input': { fontSize: 13, py: 1 },
              }}
            />
            <Button
              variant="contained"
              onClick={() => {
                onPipedriveConnect(pipedriveToken);
                setPipedriveToken('');
              }}
              disabled={!pipedriveToken.trim() || isPipedriveConnecting}
              sx={{
                backgroundColor: '#017737',
                '&:hover': { backgroundColor: '#015a2a' },
                textTransform: 'none',
              }}
            >
              {isPipedriveConnecting ? 'Connecting...' : 'Connect'}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {pipedriveStatus.companyDomain && `${pipedriveStatus.companyDomain}.pipedrive.com`}
              {pipedriveStatus.connectedAt &&
                ` — connected ${new Date(pipedriveStatus.connectedAt).toLocaleDateString()}`}
            </Typography>
            <Button
              size="small"
              color="error"
              startIcon={<LinkOffIcon />}
              onClick={onPipedriveDisconnect}
              sx={{ textTransform: 'none' }}
            >
              Disconnect
            </Button>
          </Box>
        )}
      </Box>

      {/* Smartlead */}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          p: 3,
          backgroundColor: '#fff',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <EmailIcon sx={{ fontSize: 28, color: '#6366F1' }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Smartlead
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Cold Email Campaigns
              </Typography>
            </Box>
          </Box>
          {smartleadStatus?.isConnected && (
            <Chip label="Connected" color="success" size="small" icon={<CheckCircleIcon />} />
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Connect your Smartlead account to track cold email campaign performance — emails sent,
          opens, replies, and bounce rates — in the CEOx dashboard.
        </Typography>

        {smartleadError && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {smartleadError}
          </Typography>
        )}

        {!smartleadStatus?.isConnected ? (
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <TextField
              size="small"
              placeholder="Paste your Smartlead API key"
              value={smartleadKey}
              onChange={(e) => setSmartleadKey(e.target.value)}
              type="password"
              sx={{
                flex: 1,
                maxWidth: 360,
                '& .MuiInputBase-input': { fontSize: 13, py: 1 },
              }}
            />
            <Button
              variant="contained"
              onClick={() => {
                onSmartleadConnect(smartleadKey);
                setSmartleadKey('');
              }}
              disabled={!smartleadKey.trim() || isSmartleadConnecting}
              sx={{
                backgroundColor: '#6366F1',
                '&:hover': { backgroundColor: '#4F46E5' },
                textTransform: 'none',
              }}
            >
              {isSmartleadConnecting ? 'Connecting...' : 'Connect'}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Connected
              {smartleadStatus.connectedAt &&
                ` since ${new Date(smartleadStatus.connectedAt).toLocaleDateString()}`}
            </Typography>
            <Button
              size="small"
              color="error"
              startIcon={<LinkOffIcon />}
              onClick={onSmartleadDisconnect}
              sx={{ textTransform: 'none' }}
            >
              Disconnect
            </Button>
          </Box>
        )}
      </Box>

      {/* Ahrefs */}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          p: 3,
          backgroundColor: '#fff',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PublicIcon sx={{ fontSize: 28, color: '#054AD9' }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Ahrefs
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Backlinks, Domain Rating &amp; Keyword Difficulty
              </Typography>
            </Box>
          </Box>
          {ahrefsStatus?.isConnected && (
            <Chip label="Connected" color="success" size="small" icon={<CheckCircleIcon />} />
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Connect your Ahrefs account to sync backlink profiles, domain rating, keyword difficulty
          scores, and competitor data directly into the SEO copilot.
        </Typography>

        {ahrefsError && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {ahrefsError}
          </Typography>
        )}

        {!ahrefsStatus?.isConnected ? (
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <TextField
              size="small"
              placeholder="Paste your Ahrefs API key"
              value={ahrefsKey}
              onChange={(e) => setAhrefsKey(e.target.value)}
              type="password"
              sx={{
                flex: 1,
                maxWidth: 360,
                '& .MuiInputBase-input': { fontSize: 13, py: 1 },
              }}
            />
            <Button
              variant="contained"
              onClick={() => {
                onAhrefsConnect(ahrefsKey);
                setAhrefsKey('');
              }}
              disabled={!ahrefsKey.trim() || isAhrefsConnecting}
              sx={{
                backgroundColor: '#054AD9',
                '&:hover': { backgroundColor: '#0440B8' },
                textTransform: 'none',
              }}
            >
              {isAhrefsConnecting ? 'Connecting...' : 'Connect'}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Connected
              {ahrefsStatus.connectedAt &&
                ` since ${new Date(ahrefsStatus.connectedAt).toLocaleDateString()}`}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={
                <SyncIcon
                  sx={
                    isAhrefsSyncing
                      ? {
                          animation: 'spin 1s linear infinite',
                          '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' },
                          },
                        }
                      : undefined
                  }
                />
              }
              onClick={onAhrefsSync}
              disabled={isAhrefsSyncing}
              sx={{ textTransform: 'none', borderColor: '#054AD9', color: '#054AD9' }}
            >
              {isAhrefsSyncing ? 'Syncing...' : 'Sync Now'}
            </Button>
            <Button
              size="small"
              color="error"
              startIcon={<LinkOffIcon />}
              onClick={onAhrefsDisconnect}
              sx={{ textTransform: 'none' }}
            >
              Disconnect
            </Button>
          </Box>
        )}
      </Box>

      {/* QuickBooks */}
      <Box
        sx={{
          border: '2px solid',
          borderColor: '#F4F4F5',
          borderRadius: 1.5,
          p: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1,
              backgroundColor: '#2CA01C15',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#2CA01C' }}>QB</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" fontWeight={500}>
              QuickBooks Online
            </Typography>
            {qbConnections.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {qbConnections[0].companyName}
              </Typography>
            )}
          </Box>
          {qbConnections.length > 0 && (
            <Chip
              label="Connected"
              size="small"
              color="success"
              variant="outlined"
              sx={{ ml: 'auto', fontSize: 10 }}
            />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Sync invoices and transactions from QuickBooks Online into FinanceX.
        </Typography>

        {qbConnections.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {qbConnections.map((conn) => (
              <Box key={conn.id} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Realm: {conn.realmId} | Last synced:{' '}
                  {conn.lastSyncedAt ? new Date(conn.lastSyncedAt).toLocaleString() : 'Never'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={
                      isQbSyncing ? (
                        <CircularProgress size={14} />
                      ) : (
                        <SyncIcon sx={{ fontSize: 14 }} />
                      )
                    }
                    disabled={isQbSyncing}
                    onClick={() => onQbSync(conn.id)}
                    sx={{ textTransform: 'none', fontSize: 12 }}
                  >
                    {isQbSyncing ? 'Syncing...' : 'Sync All'}
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<LinkOffIcon sx={{ fontSize: 14 }} />}
                    onClick={() => onQbDisconnect(conn.id)}
                    sx={{ textTransform: 'none', fontSize: 12, color: '#dc2626' }}
                  >
                    Disconnect
                  </Button>
                </Box>
              </Box>
            ))}
            {qbSyncResult && (
              <Typography
                variant="caption"
                sx={{ mt: 1, color: qbSyncResult.startsWith('Error') ? '#dc2626' : '#16a34a' }}
              >
                {qbSyncResult}
              </Typography>
            )}
          </Box>
        ) : (
          <Button
            size="small"
            variant="contained"
            onClick={onQbConnect}
            disabled={isQbConnecting}
            sx={{
              textTransform: 'none',
              fontSize: 12,
              backgroundColor: '#2CA01C',
              '&:hover': { backgroundColor: '#248a16' },
            }}
          >
            {isQbConnecting ? 'Connecting...' : 'Connect QuickBooks'}
          </Button>
        )}
      </Box>

      {/* Warp — contractor & employee roster */}
      <Box
        sx={{
          border: '2px solid',
          borderColor: '#F4F4F5',
          borderRadius: 1.5,
          p: 3,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                backgroundColor: '#054AD915',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#054AD9' }}>W</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Warp
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Contractor &amp; US payroll roster
              </Typography>
            </Box>
          </Box>
          {warpStatus?.isConnected && (
            <Chip label="Connected" color="success" size="small" icon={<CheckCircleIcon />} />
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Sync your Warp worker directory (contractors &amp; employees) into FinanceX. Warp&apos;s
          API does not expose payment amounts — those still come from the payroll CSV / QuickBooks.
          Use <strong>Link to Vendors</strong> to create matching vendor records for reconciliation.
        </Typography>

        {warpError && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {warpError}
          </Typography>
        )}

        {!warpStatus?.isConnected ? (
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <TextField
              size="small"
              placeholder="Paste your Warp API key"
              value={warpKey}
              onChange={(e) => setWarpKey(e.target.value)}
              type="password"
              sx={{ flex: 1, maxWidth: 360, '& .MuiInputBase-input': { fontSize: 13, py: 1 } }}
            />
            <Button
              variant="contained"
              onClick={() => {
                onWarpConnect(warpKey);
                setWarpKey('');
              }}
              disabled={!warpKey.trim() || isWarpConnecting}
              sx={{
                backgroundColor: '#054AD9',
                '&:hover': { backgroundColor: '#0440B8' },
                textTransform: 'none',
              }}
            >
              {isWarpConnecting ? 'Connecting...' : 'Connect'}
            </Button>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {warpStatus.lastSyncedAt
                  ? `Last synced ${new Date(warpStatus.lastSyncedAt).toLocaleString()}`
                  : 'Not synced yet'}
                {warpStatus.workerCount ? ` · ${warpStatus.workerCount} workers` : ''}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={isWarpSyncing ? <CircularProgress size={14} /> : <SyncIcon />}
                onClick={onWarpSync}
                disabled={isWarpSyncing}
                sx={{ textTransform: 'none', borderColor: '#054AD9', color: '#054AD9' }}
              >
                {isWarpSyncing ? 'Syncing...' : 'Sync Now'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={isWarpLinking ? <CircularProgress size={14} /> : <AddIcon />}
                onClick={onWarpLink}
                disabled={isWarpLinking}
                sx={{ textTransform: 'none', borderColor: '#054AD9', color: '#054AD9' }}
              >
                {isWarpLinking ? 'Linking...' : 'Link to Vendors'}
              </Button>
              <Button
                size="small"
                color="error"
                startIcon={<LinkOffIcon />}
                onClick={onWarpDisconnect}
                sx={{ textTransform: 'none' }}
              >
                Disconnect
              </Button>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={warpCreateProfiles}
                  onChange={(e) => onWarpCreateProfilesChange(e.target.checked)}
                />
              }
              label="Create profiles for new people not found by personal or work email"
              sx={{
                display: 'block',
                mb: 1,
                '& .MuiFormControlLabel-label': { fontSize: 13, color: '#4A4B4E' },
              }}
            />

            {warpResult && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mb: 2,
                  color: warpResult.startsWith('Error') ? '#dc2626' : '#16a34a',
                }}
              >
                {warpResult}
              </Typography>
            )}

            {warpContractors.length > 0 && (
              <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <Box
                  component="table"
                  sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}
                >
                  <Box component="thead">
                    <Box component="tr" sx={{ textAlign: 'left', color: '#71717A' }}>
                      {['Name', 'Position', 'Status', 'Department', 'Start', 'Linked'].map((h) => (
                        <Box
                          component="th"
                          key={h}
                          sx={{
                            py: 1,
                            px: 1,
                            fontWeight: 500,
                            borderBottom: '1px solid hsla(0,0%,0%,0.08)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {h}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {warpContractors.map((w) => (
                      <Box component="tr" key={w.id}>
                        <Box
                          component="td"
                          sx={{
                            py: 1,
                            px: 1,
                            borderBottom: '1px solid hsla(0,0%,0%,0.08)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {w.displayName ||
                            [w.firstName, w.lastName].filter(Boolean).join(' ') ||
                            w.businessName ||
                            '—'}
                        </Box>
                        <Box
                          component="td"
                          sx={{ py: 1, px: 1, borderBottom: '1px solid hsla(0,0%,0%,0.08)' }}
                        >
                          {w.position || '—'}
                        </Box>
                        <Box
                          component="td"
                          sx={{ py: 1, px: 1, borderBottom: '1px solid hsla(0,0%,0%,0.08)' }}
                        >
                          {w.status || '—'}
                        </Box>
                        <Box
                          component="td"
                          sx={{ py: 1, px: 1, borderBottom: '1px solid hsla(0,0%,0%,0.08)' }}
                        >
                          {w.departmentName || '—'}
                        </Box>
                        <Box
                          component="td"
                          sx={{
                            py: 1,
                            px: 1,
                            borderBottom: '1px solid hsla(0,0%,0%,0.08)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {w.startDate ? new Date(w.startDate).toLocaleDateString() : '—'}
                        </Box>
                        <Box
                          component="td"
                          sx={{ py: 1, px: 1, borderBottom: '1px solid hsla(0,0%,0%,0.08)' }}
                        >
                          <WarpLinkBadge worker={w} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Future integrations placeholder */}
      <Box
        sx={{
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1.5,
          p: 3,
          backgroundColor: '#fafafa',
          opacity: 0.6,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Typography variant="subtitle2" fontWeight={500} color="text.secondary">
            More integrations coming soon
          </Typography>
          <Chip label="Coming Soon" size="small" variant="outlined" sx={{ fontSize: 10 }} />
        </Box>
        <Typography variant="caption" color="text.secondary">
          Slack, HubSpot, and more will be available here in the future.
        </Typography>
      </Box>
    </Box>
  );
};

export default IntegrationsComponent;
